/**
 * AuthCallbackPage.tsx — Supabase email-confirmation callback handler.
 *
 * Supabase redirects here after a user clicks the confirmation link in their
 * email.  The URL will contain an auth code (PKCE flow) or a hash fragment
 * (implicit flow) that @supabase/supabase-js resolves automatically because
 * detectSessionInUrl=true is set in the client config.
 *
 * Auth Stabilization Phase 1 — what this page does:
 *  1. Waits for supabase.auth.onAuthStateChange to fire SIGNED_IN.
 *  2. Eagerly calls GET /api/auth/me with the new user's Bearer token to
 *     provision the internal WorkRoamer user record BEFORE navigating. This
 *     guarantees the internal user row exists when Shell mounts, eliminating
 *     the race where Shell's first query would fail with 401.
 *  3. Uses the API response (onboardingCompleted field) as the authoritative
 *     source of truth for whether the user is new or returning — not localStorage.
 *     New user (onboardingCompleted=false) → sets 'nomad:fresh-signup' flag.
 *     Returning user (onboardingCompleted=true) → no flag, Shell goes to Home.
 *  4. Clears the TanStack Query cache before navigating so no previous user's
 *     data can contaminate Shell's boot decision for the incoming user.
 *  5. Redirects to /home on success, or to /login with an error on failure.
 *
 * Session isolation guarantee (Bug fix — new-user onboarding bypass):
 *  Root cause: bare fetch('/api/auth/me') had no Authorization header → the
 *  server fell back to the previous user's session cookie → returned the old
 *  user's onboardingCompleted:true → fresh-signup flag was never set → new
 *  user was dropped into the previous user's workspace.
 *
 *  Fix: the provisioning fetch now injects `Authorization: Bearer <token>`
 *  directly from the session object passed by onAuthStateChange (no getSession()
 *  re-call needed — the session is already resolved at this point). Additionally,
 *  queryClient.clear() is called before any navigation so Shell always starts
 *  with an empty cache, regardless of who was logged in before.
 *
 * Route: /auth/callback  (registered in App.tsx)
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

type Phase = 'checking' | 'provisioning' | 'success' | 'error';

// Resolve the API base URL from the Vite environment.
// In dev, Vite proxies /api → api-server. In production, the Replit routing
// handles it. We never hardcode localhost.
function getApiBase(): string {
  // The API is always mounted at /api relative to the origin, regardless of
  // the app's BASE_URL (which prefixes SPA routes, not API routes).
  return '/api';
}

export default function AuthCallbackPage() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [errMsg, setErrMsg] = useState('');
  const [, setLocation] = useLocation();

  // Used to flush stale previous-user data before Shell mounts.
  const queryClient = useQueryClient();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    // supabase-js automatically parses the URL hash / code param on load when
    // detectSessionInUrl=true. We just listen for the resulting auth event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
          // ── Phase 1: Eager internal user provisioning ─────────────────────
          // Inject the Bearer token from the session object that Supabase
          // passes directly to this handler. At this point the session is
          // already resolved — we do NOT call getSession() again (that can
          // return a stale or null value depending on SDK internals).
          //
          // IMPORTANT: using Authorization: Bearer here is what prevents the
          // server from falling back to a previous user's session cookie.
          // A bare fetch() with no auth header hit this exact bug: the server
          // resolved the old cookie-session → returned the old user's
          // onboardingCompleted:true → fresh-signup flag was never set.
          setPhase('provisioning');

          const accessToken = session.access_token;

          try {
            const apiBase = getApiBase();
            const res = await fetch(`${apiBase}/auth/me`, {
              credentials: 'include',
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
            });

            if (res.ok) {
              const user = await res.json() as {
                id?: number;
                onboardingCompleted?: boolean;
              };

              // ── Phase 2: Determine new vs returning user ──────────────────
              // Server is the authority. If onboardingCompleted is false, this
              // is a new account — flag it so Shell opens the wizard exactly once.
              // If true, the wizard has already been completed; Shell goes straight
              // to Home. Never read from localStorage here.
              if (!user.onboardingCompleted) {
                try {
                  sessionStorage.setItem('nomad:fresh-signup', '1');
                } catch { /* sessionStorage unavailable — wizard via hydration path */ }
              }

              console.log(
                `[AuthCallback] provisioning ok — ` +
                `userId=${user.id ?? 'unknown'} ` +
                `onboardingCompleted=${user.onboardingCompleted ?? 'unknown'} ` +
                `isFreshSignup=${!user.onboardingCompleted}`,
              );
            } else {
              // Non-200 from /auth/me — log but do NOT set fresh-signup.
              // ONBOARDING LOOP FIX: the conservative fallback (setting fresh-signup
              // on any error) was causing returning users to be sent back to the
              // wizard when this fetch had a transient failure. Shell's cloud
              // hydration path is the correct fallback — it reads the cloud persona
              // (onboardingCompleted:true) and correctly routes returning users to
              // Home without wizard. Fresh-signup is only set when the server
              // explicitly confirms the account is new.
              console.warn(
                `[AuthCallback] GET /auth/me returned ${res.status} — ` +
                `NOT marking fresh-signup; Shell cloud hydration will decide.`,
              );
            }
          } catch (fetchErr) {
            // Network error — same: do NOT set fresh-signup conservatively.
            // Shell cloud hydration handles returning users on network hiccups.
            console.warn('[AuthCallback] fetch /auth/me error (not marking fresh-signup):', fetchErr);
          }

          // ── Phase 3: Session isolation — flush stale cache ────────────────
          // Clear the entire TanStack Query cache before navigating. This
          // guarantees that Shell's boot effect never sees a previous user's
          // cached /auth/me data (which would have onboardingCompleted:true
          // for a returning owner and cause the new user to inherit their
          // workspace).
          //
          // queryClient.clear() removes ALL cached data. useGetMe() will
          // refetch fresh on Shell mount, Shell's boot effect will wait for
          // the result (apiUser undefined → early-return guard), and only
          // fire decideBootExperience once the correct new-user data arrives.
          try { queryClient.clear(); } catch { /* ignore */ }

          setPhase('success');
          // Brief success flash before redirecting.
          timer = setTimeout(() => setLocation('/home'), 1200);
        }
      },
    );

    // Fallback: if no event fires within 8 s, check current session.
    const fallback = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        // Same cache flush as the main path — we don't know who was logged
        // in before, so start clean regardless.
        try { queryClient.clear(); } catch { /* ignore */ }
        setPhase('success');
        timer = setTimeout(() => setLocation('/home'), 800);
      } else {
        setPhase('error');
        setErrMsg('The confirmation link may have expired. Please request a new one.');
      }
    }, 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  // queryClient is stable (never changes reference) — safe in deps.
  }, [setLocation, queryClient]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse 80% 70% at 50% 35%, #0b2060 0%, #04112b 55%, #010a1a 100%)',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
        textAlign: 'center', padding: '40px 32px',
        background: 'linear-gradient(175deg, #0d2048 0%, #071428 60%, #040d1e 100%)',
        borderRadius: 20, border: '1.5px solid rgba(59,130,246,0.35)',
        boxShadow: '0 0 40px rgba(37,99,235,0.25)',
        maxWidth: 380, width: '90%',
      }}>
        {/* Brand */}
        <img
          src="/workroamer-brand.png"
          alt="WorkRoamer"
          style={{ height: 40, width: 'auto', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />

        {phase === 'checking' && (
          <>
            <Loader2 size={40} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
                Confirming your account…
              </p>
              <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
                Just a moment while we verify your email address.
              </p>
            </div>
          </>
        )}

        {phase === 'provisioning' && (
          <>
            <Loader2 size={40} color="#8b5cf6" style={{ animation: 'spin 1s linear infinite' }} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
                Setting up your account…
              </p>
              <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
                Initializing your WorkRoamer profile.
              </p>
            </div>
          </>
        )}

        {phase === 'success' && (
          <>
            <CheckCircle2 size={48} color="#22c55e" strokeWidth={1.5}
              style={{ filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.5))' }} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
                Email confirmed!
              </p>
              <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
                Taking you to WorkRoamer…
              </p>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <XCircle size={48} color="#f87171" strokeWidth={1.5} />
            <div>
              <p style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
                Confirmation failed
              </p>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
                {errMsg}
              </p>
              <a
                href="/login"
                style={{
                  display: 'inline-block', padding: '10px 24px',
                  background: 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 100%)',
                  color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  textDecoration: 'none', letterSpacing: '0.05em',
                }}
              >
                Back to Login
              </a>
            </div>
          </>
        )}
      </div>

      {/* CSS animation for the spinner */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
